import { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { useGlobalConfig } from '../../common/utils/global-config';
import type { TwilioLiveChatMessage } from '../../controllers/chat-store/twilio-live-chat';
import { type InteractionChannel, useAssistanceService } from '../../services/assistance-service';

import {
	type ChatConfigurationResponse,
	type DirectLiveChatResponse,
	type GetAuthHeadersFn,
	type InvalidateAuthFn,
	type TwilioConversationDetailsResponse,
	closeTwilioConversation,
	createDirectLiveChatConversation,
	fetchAgentName,
	fetchTokenAndBuildMeta,
	getChatConfiguration,
	getConversationMessages,
	getTwilioConversationDetails,
	listOmniConversations,
} from './ocg-client';
import type { TwilioClientTokenMeta } from './ocg-client-store';
import type { OmniConversationsResponse } from './ocg-types';

/**
 * Bound OCG client. Each method captures the `gatewayOrigin` +
 * {@link GetAuthHeadersFn} supplied to {@link createOcgClient} so callers no
 * longer pass them at every call site. The methods otherwise match the
 * standalone exports in {@link './ocg-client'} 1:1.
 *
 * Use this from React via {@link useOcgClient}, or pass it explicitly into
 * `react-sweet-state` actions / Twilio SDK lifecycle handlers that must run
 * outside a React render.
 */
export type OcgClient = {
	fetchAgentName: (agentUuid: string) => Promise<string>;
	fetchTokenAndBuildMeta: (helpCenterId: string) => Promise<TwilioClientTokenMeta>;
	createDirectLiveChatConversation: (
		helpCenterId: string,
		firstMessage: DocNode,
		metadata?: Record<string, string>,
		channel?: InteractionChannel,
	) => Promise<DirectLiveChatResponse>;
	getChatConfiguration: (helpCenterId: string) => Promise<ChatConfigurationResponse>;
	closeTwilioConversation: (twilioConversationId: string) => Promise<void>;
	getTwilioConversationDetails: (
		twilioConversationSid: string,
	) => Promise<TwilioConversationDetailsResponse>;
	getConversationMessages: (interactionId: string) => Promise<TwilioLiveChatMessage[]>;
	listOmniConversations: (helpCenterId: string) => Promise<OmniConversationsResponse>;
};

/** Constructor dependencies for {@link createOcgClient} / {@link useOcgClient}. */
export type OcgClientDeps = {
	gatewayOrigin?: string;
	getAuthHeaders: GetAuthHeadersFn;
	/**
	 * Called to clear cached credentials in the underlying auth provider before
	 * the 401 retry inside {@link request}. Without it, providers that cache
	 * tokens (notably `ExternalOAuthProvider`) re-issue the same stale `Bearer`
	 * token on the retry, defeating the recovery mechanism. See BEARS-1804.
	 */
	invalidateAuth: InvalidateAuthFn;
	credentials?: RequestCredentials;
};

/**
 * Build an {@link OcgClient} bound to a specific {@link OcgClientDeps#gatewayOrigin}
 * and {@link OcgClientDeps#getAuthHeaders}. Each method invokes the underlying
 * `request` helper inside `./ocg-client` (which transparently retries once
 * on 401 with refreshed headers — BEARS-1804) and forwards to the relevant
 * standalone function.
 *
 * Prefer {@link useOcgClient} when calling from a React render. This factory
 * is provided so non-React callers (`react-sweet-state` action payloads,
 * Twilio SDK token-refresh handlers, etc.) can be handed the same shape.
 *
 * Tests can either inject `useOcgClient` directly via `react-magnetic-di`,
 * or stub the individual exports of `./ocg-client` with `jest.mock` —
 * because the factory references them via cross-module imports, jest's
 * mock binding is honoured here (unlike same-file references).
 */
export const createOcgClient = ({
	gatewayOrigin,
	getAuthHeaders,
	invalidateAuth,
	credentials,
}: OcgClientDeps): OcgClient => ({
	fetchAgentName: (agentUuid) =>
		fetchAgentName(agentUuid, gatewayOrigin, getAuthHeaders, invalidateAuth, credentials),
	fetchTokenAndBuildMeta: (helpCenterId) =>
		fetchTokenAndBuildMeta(
			helpCenterId,
			gatewayOrigin,
			getAuthHeaders,
			invalidateAuth,
			credentials,
		),
	createDirectLiveChatConversation: (helpCenterId, firstMessage, metadata, channel) =>
		createDirectLiveChatConversation(
			helpCenterId,
			firstMessage,
			gatewayOrigin,
			metadata,
			getAuthHeaders,
			invalidateAuth,
			channel,
			credentials,
		),
	getChatConfiguration: (helpCenterId) =>
		getChatConfiguration(helpCenterId, gatewayOrigin, getAuthHeaders, invalidateAuth, credentials),
	closeTwilioConversation: (twilioConversationId) =>
		closeTwilioConversation(
			twilioConversationId,
			gatewayOrigin,
			getAuthHeaders,
			invalidateAuth,
			credentials,
		),
	getTwilioConversationDetails: (twilioConversationSid) =>
		getTwilioConversationDetails(
			twilioConversationSid,
			gatewayOrigin,
			getAuthHeaders,
			invalidateAuth,
			credentials,
		),
	getConversationMessages: (interactionId) =>
		getConversationMessages(
			interactionId,
			gatewayOrigin,
			getAuthHeaders,
			invalidateAuth,
			credentials,
		),
	listOmniConversations: (helpCenterId) =>
		listOmniConversations(helpCenterId, gatewayOrigin, getAuthHeaders, invalidateAuth, credentials),
});

/**
 * React hook returning a {@link OcgClient} bound to the current
 * `gatewayOrigin` (from {@link useGlobalConfig}) and the current
 * `assistanceService.getAuthHeaders` (from {@link useAssistanceService}).
 *
 * Each method on the returned client transparently:
 * - resolves a fresh `Authorization` header via
 *   `assistanceService.getAuthHeaders()` immediately before sending;
 * - on a 401 response, resolves the header again and retries the request
 *   exactly once (BEARS-1804).
 *
 * Callers no longer need to thread `gatewayOrigin` or pre-fetched auth
 * headers through every callsite.
 *
 * The returned client is referentially stable across renders as long as both
 * `gatewayOrigin` and the assistance-service instance are stable, which is
 * the steady-state for the widget. New callers (`react-sweet-state` actions,
 * Twilio SDK lifecycle handlers) that need to use the same client outside a
 * React render should accept the {@link OcgClient} as an explicit dependency
 * (typically via the action payload) so the React layer can hand it in.
 *
 * Both `useGlobalConfig` and `useAssistanceService` throw when their
 * provider is missing — that's the production contract and we propagate it
 * here. Tests that mount a component depending on `useOcgClient` without
 * the full provider tree must inject this hook via
 * `di(useOcgClient, …)` and supply a stub.
 *
 * `assistanceService.getAuthHeaders` already coalesces a missing
 * `authProvider` to `null` (see `AssistanceServiceCsm.getAuthHeaders`), so
 * we can pass it through unconditionally — it will never throw.
 */
export const useOcgClient = (): OcgClient => {
	const { gatewayOrigin } = useGlobalConfig();
	const { getAuthHeaders, invalidateAuth, getConfig } = useAssistanceService();
	const { credentials } = getConfig();

	return useMemo(
		() =>
			createOcgClient({
				gatewayOrigin,
				getAuthHeaders,
				invalidateAuth,
				credentials,
			}),
		[gatewayOrigin, getAuthHeaders, invalidateAuth, credentials],
	);
};
